import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Loader2, Save, X } from "lucide-react";
import { adminAxios, formatApiError } from "../../lib/adminApi";
import { Input } from "../../components/ui/input";
import { Textarea } from "../../components/ui/textarea";
import { Label } from "../../components/ui/label";

export default function AdminServices() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await adminAxios.get("/admin/services");
        setItems(data.services || []);
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const save = async (idx, data) => {
    try {
      await adminAxios.put(`/admin/services/${data.sid}`, {
        sid: data.sid,
        title: data.title,
        short: data.short,
        description: data.description,
        bullets: data.bullets,
        image: data.image,
        order: data.order ?? idx,
      });
      toast.success("Saved");
      setItems((arr) => arr.map((s, i) => (i === idx ? data : s)));
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  return (
    <div data-testid="admin-services-page" className="space-y-6">
      <div>
        <h1 className="font-display text-2xl md:text-3xl font-bold text-slate-900">Services</h1>
        <p className="text-slate-500 text-sm mt-1">Edit the 4 service pillars shown across the site.</p>
      </div>

      {loading ? (
        <div className="py-20 flex justify-center"><Loader2 className="w-5 h-5 animate-spin text-[#1C6B8A]" /></div>
      ) : (
        <div className="space-y-5" data-testid="services-list">
          {items.map((s, idx) => (
            <ServiceEditor key={s.sid} initial={s} onSave={(d) => save(idx, d)} />
          ))}
        </div>
      )}
    </div>
  );
}

function ServiceEditor({ initial, onSave }) {
  const [s, setS] = useState(initial);
  const [newBullet, setNewBullet] = useState("");

  const addBullet = () => {
    if (!newBullet.trim()) return;
    setS({ ...s, bullets: [...(s.bullets || []), newBullet.trim()] });
    setNewBullet("");
  };

  const removeBullet = (i) => {
    setS({ ...s, bullets: s.bullets.filter((_, idx) => idx !== i) });
  };

  return (
    <div className="bg-white rounded-2xl border border-slate-100 p-5 card-soft" data-testid={`service-card-${s.sid}`}>
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-3">
          <div className="text-xs font-mono text-slate-400">sid: {s.sid}</div>
        </div>
      </div>
      <div className="grid md:grid-cols-2 gap-3">
        <div className="space-y-1.5">
          <Label>Title</Label>
          <Input value={s.title} onChange={(e) => setS({ ...s, title: e.target.value })} data-testid={`svc-title-${s.sid}`} />
        </div>
        <div className="space-y-1.5">
          <Label>Image URL</Label>
          <Input value={s.image || ""} onChange={(e) => setS({ ...s, image: e.target.value })} placeholder="https://..." />
        </div>
      </div>
      <div className="space-y-1.5 mt-3">
        <Label>Short tagline</Label>
        <Input value={s.short || ""} onChange={(e) => setS({ ...s, short: e.target.value })} />
      </div>
      <div className="space-y-1.5 mt-3">
        <Label>Description</Label>
        <Textarea rows={3} value={s.description || ""} onChange={(e) => setS({ ...s, description: e.target.value })} />
      </div>
      <div className="space-y-1.5 mt-3">
        <Label>Bullets</Label>
        <div className="flex flex-wrap gap-2 mb-2">
          {(s.bullets || []).map((b, i) => (
            <span key={i} className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-slate-100 text-sm">
              {b}
              <button onClick={() => removeBullet(i)} className="hover:text-red-600">
                <X className="w-3 h-3" />
              </button>
            </span>
          ))}
        </div>
        <div className="flex gap-2">
          <Input value={newBullet} onChange={(e) => setNewBullet(e.target.value)} placeholder="Add bullet..." onKeyDown={(e) => e.key === "Enter" && (e.preventDefault(), addBullet())} />
          <button onClick={addBullet} className="h-10 px-4 rounded-full brand-outline-btn text-xs font-semibold">Add</button>
        </div>
      </div>
      <button
        onClick={() => onSave(s)}
        data-testid={`svc-save-${s.sid}`}
        className="mt-4 h-10 px-5 rounded-full brand-btn text-sm font-semibold inline-flex items-center gap-2"
      >
        <Save className="w-4 h-4" /> Save service
      </button>
    </div>
  );
}
